import type { PrescriptionPrintSettingsUI } from '@helios/data-access';
import { orgLogoUrl, orgLogoHorizontalUrl } from '../../../shell/shared';
import { getSystemTemplate, type SystemTemplate } from './system-templates';

/** Resolve the configured logo choice to its actual URL (build-time env). */
function resolveLogoUrl(settings: PrescriptionPrintSettingsUI): string {
  switch (settings.logoSource) {
    case 'standard':
      return orgLogoUrl;
    case 'horizontal':
      return orgLogoHorizontalUrl;
    case 'none':
    default:
      return '';
  }
}

/**
 * PRD.184 §7 — Generated Prescription Print Layout.
 *
 * Renders a clean, printable prescription page from:
 *   - the saved Prescription Print settings (template + branding + sections)
 *   - the clinic / doctor branding (with app_config defaults applied upstream)
 *   - the patient header data
 *   - the structured prescription rows
 *
 * The renderer is purely presentational. It produces an A4-shaped surface
 * suitable for the wizard's Preview step and for actual `window.print()` use
 * once the patient workflow wires it in. No database calls here.
 */

// ─── Public types ───

export interface PrintPatient {
  name: string;
  age?: string;
  sex?: string;
}

export interface PrintPrescriptionItem {
  itemName: string;
  itemDetails?: string;
  intakeTiming?: string;
  doseQuantity?: string;
  scheduleMorning: boolean;
  scheduleAfternoon: boolean;
  scheduleNight: boolean;
  durationValue?: number | null;
  durationUnit?: string;
  instructions?: string;
}

/**
 * Doctor identity for the printout. Resolved by the caller from the printing
 * user's linked provider (user_provider_links → appointment_providers) — this
 * is per-user, so it is intentionally NOT part of the saved org-default
 * `PrescriptionPrintSettingsUI`.
 */
export interface PrintDoctor {
  name?: string;
  credentials?: string;
}

export interface PrescriptionPrintLayoutProps {
  settings: PrescriptionPrintSettingsUI;
  doctor?: PrintDoctor;
  patient: PrintPatient;
  date: string; // ISO
  items: PrintPrescriptionItem[];
  /** Optional summary blocks, only shown if the template + settings allow them. */
  complaint?: string;
  vitalsSummary?: string;
  /** Optional weight/BP shown on the Classic Clinical patient strip. */
  weightAndBp?: string;
  /**
   * Brand accent color used by templates that theme the items table header
   * and the in-page rules (Classic Clinical). Defaults to a clinical maroon.
   */
  accentColor?: string;
  /** When true, the layout is rendered at preview density instead of A4 paper size. */
  previewMode?: boolean;
}

// ─── Constants ───

const INTAKE_TIMING_LABEL: Record<string, string> = {
  before_food: 'Before Food',
  after_food: 'After Food',
  with_food: 'With Food',
  empty_stomach: 'Empty Stomach',
  anytime: 'Anytime',
};

const DURATION_UNIT_LABEL: Record<string, string> = {
  days: 'Days',
  weeks: 'Weeks',
  months: 'Months',
};

// ─── Helpers ───

function formatScheduleSummary(item: PrintPrescriptionItem): string {
  const parts: string[] = [];
  if (item.scheduleMorning) parts.push('M');
  if (item.scheduleAfternoon) parts.push('A');
  if (item.scheduleNight) parts.push('N');
  if (parts.length === 0) return '—';
  return parts.join(' / ');
}

function formatDuration(item: PrintPrescriptionItem): string {
  if (item.durationValue == null) return '—';
  const unit = DURATION_UNIT_LABEL[item.durationUnit ?? 'days'] ?? item.durationUnit ?? 'Days';
  return `${item.durationValue} ${unit}`;
}

function formatTiming(value: string | undefined): string {
  if (!value) return '—';
  return INTAKE_TIMING_LABEL[value] ?? value;
}

function formatPrintDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ─── Component ───

const DEFAULT_ACCENT = '#5A2A18';

export function PrescriptionPrintLayout({
  settings,
  doctor,
  patient,
  date,
  items,
  complaint,
  vitalsSummary,
  weightAndBp,
  accentColor,
  previewMode,
}: PrescriptionPrintLayoutProps) {
  const template = getSystemTemplate(settings.templateId);
  const compact = template.density === 'compact';
  const doctorName = doctor?.name ?? '';
  const doctorCredentials = doctor?.credentials ?? '';
  const accent = accentColor || DEFAULT_ACCENT;

  // The Classic Clinical template uses its own dedicated render path.
  if (settings.templateId === 'classic_clinical') {
    return (
      <ClassicClinicalLayout
        settings={settings}
        doctorName={doctorName}
        doctorCredentials={doctorCredentials}
        patient={patient}
        date={date}
        items={items}
        complaint={complaint}
        weightAndBp={weightAndBp}
        accent={accent}
        previewMode={previewMode}
      />
    );
  }

  const pagePadding = compact ? 28 : 40;
  const sectionGap = compact ? 16 : 22;
  const baseFont = compact ? 12 : 13;

  return (
    <div
      data-prescription-print="true"
      style={{
        // Always render at A4 dimensions so the on-screen preview matches what
        // actually prints. The PreviewPane scales this down to fit the wizard's
        // right-hand column via `transform: scale()`.
        width: '210mm',
        minHeight: '297mm',
        background: '#ffffff',
        color: '#0f172a',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        fontSize: baseFont,
        lineHeight: 1.45,
        padding: pagePadding,
        boxSizing: 'border-box',
        boxShadow: previewMode ? '0 1px 3px rgba(15, 23, 42, 0.12)' : undefined,
        border: previewMode ? '1px solid rgba(15, 23, 42, 0.08)' : undefined,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: sectionGap,
      }}
    >
      <Header
        settings={settings}
        template={template}
        doctorName={doctorName}
        doctorCredentials={doctorCredentials}
      />

      <PatientStrip
        settings={settings}
        patient={patient}
        date={date}
        showRxSymbol={template.showRxSymbol}
      />

      {settings.showComplaint && complaint && <Block label="Complaint">{complaint}</Block>}
      {settings.showVitals && vitalsSummary && <Block label="Vitals">{vitalsSummary}</Block>}

      <ItemsBlock template={template} settings={settings} items={items} />

      <Footer
        settings={settings}
        template={template}
        doctorName={doctorName}
        doctorCredentials={doctorCredentials}
      />
    </div>
  );
}

// ─── Summary block (complaint / vitals) ───

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'baseline',
        fontSize: 12,
      }}
    >
      <span
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#64748b',
          fontWeight: 600,
          minWidth: 78,
        }}
      >
        {label}
      </span>
      <span style={{ color: '#0f172a', flex: 1 }}>{children}</span>
    </div>
  );
}

// ─── Header ───

function Header({
  settings,
  template,
  doctorName,
  doctorCredentials,
}: {
  settings: PrescriptionPrintSettingsUI;
  template: SystemTemplate;
  doctorName: string;
  doctorCredentials: string;
}) {
  const align = template.headerAlign;
  const logoUrl = resolveLogoUrl(settings);
  const isHorizontal = settings.logoSource === 'horizontal';
  const logoMaxHeight = template.prominentLogo ? (isHorizontal ? 56 : 64) : isHorizontal ? 44 : 44;
  const logoMaxWidthCenter = isHorizontal ? 320 : 220;
  const logoMaxWidthInline = isHorizontal ? 240 : 160;

  // Clinic text block. Each line is conditional, but the wrapper itself is
  // always rendered so the logo's flex sibling stays stable across edits —
  // otherwise toggling clinicName empty/non-empty would unmount the wrapper
  // and force the img to remount, which can flicker or collapse in flex.
  const clinicBlock = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: align === 'center' ? 'center' : 'flex-start',
        gap: 2,
      }}
    >
      {settings.clinicName && (
        <div
          style={{
            fontSize: template.prominentLogo ? 18 : 16,
            fontWeight: 700,
            color: '#0f172a',
          }}
        >
          {settings.clinicName}
        </div>
      )}
      {settings.clinicAddress && (
        <div style={{ color: '#475569', fontSize: 11 }}>{settings.clinicAddress}</div>
      )}
      {(settings.clinicPhone || settings.clinicEmail) && (
        <div style={{ color: '#475569', fontSize: 11 }}>
          {[settings.clinicPhone, settings.clinicEmail].filter(Boolean).join('  ·  ')}
        </div>
      )}
    </div>
  );

  // Definite height + width:auto keeps the image from collapsing when it's the
  // only child of its flex container. Browsers compute width from the intrinsic
  // aspect ratio; maxWidth caps very wide horizontal logos.
  const logoStyle: React.CSSProperties = {
    display: 'block',
    flexShrink: 0,
    objectFit: 'contain',
    height: logoMaxHeight,
    width: 'auto',
  };

  const doctorInline =
    template.doctorBlock === 'header_below_clinic' && (doctorName || doctorCredentials) ? (
      <div
        style={{
          marginTop: 6,
          color: '#0f172a',
          fontSize: 12,
          textAlign: align,
        }}
      >
        {doctorName && <strong>{doctorName}</strong>}
        {doctorName && doctorCredentials && ', '}
        {doctorCredentials && <span style={{ color: '#475569' }}>{doctorCredentials}</span>}
      </div>
    ) : null;

  // Centered (Clean Classic) — stack: logo, clinic, doctor.
  if (align === 'center') {
    return (
      <header
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          paddingBottom: 12,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Clinic logo"
            style={{ ...logoStyle, maxWidth: logoMaxWidthCenter }}
          />
        )}
        {clinicBlock}
        {doctorInline}
      </header>
    );
  }

  // Left-aligned (Compact Modern, Doctor Letterhead, Minimal) — split layout.
  const showDoctorRight =
    template.doctorBlock === 'header_right' && (doctorName || doctorCredentials);

  return (
    <header
      style={{
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingBottom: 12,
        borderBottom: '1px solid #e2e8f0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Clinic logo"
            style={{ ...logoStyle, maxWidth: logoMaxWidthInline }}
          />
        )}
        {clinicBlock}
      </div>
      {showDoctorRight && (
        <div style={{ textAlign: 'right' }}>
          {doctorName && <div style={{ fontWeight: 700, fontSize: 13 }}>{doctorName}</div>}
          {doctorCredentials && (
            <div style={{ fontSize: 11, color: '#475569' }}>{doctorCredentials}</div>
          )}
        </div>
      )}
    </header>
  );
}

// ─── Patient strip ───

function PatientStrip({
  settings,
  patient,
  date,
  showRxSymbol,
}: {
  settings: PrescriptionPrintSettingsUI;
  patient: PrintPatient;
  date: string;
  showRxSymbol: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 16,
      }}
    >
      <div>
        {settings.showPatientDemographics ? (
          <>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>
              Patient
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{patient.name || '—'}</div>
            {(patient.age || patient.sex) && (
              <div style={{ fontSize: 11, color: '#475569' }}>
                {[patient.age, patient.sex].filter(Boolean).join(' · ')}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 11, color: '#64748b' }}>Prescription</div>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        {showRxSymbol && (
          <div
            aria-hidden
            style={{
              fontFamily: '"Times New Roman", serif',
              fontStyle: 'italic',
              fontSize: 30,
              fontWeight: 700,
              lineHeight: 1,
              color: '#0f172a',
            }}
          >
            ℞
          </div>
        )}
        <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{formatPrintDate(date)}</div>
      </div>
    </div>
  );
}

// ─── Items ───

function ItemsBlock({
  template,
  settings,
  items,
}: {
  template: SystemTemplate;
  settings: PrescriptionPrintSettingsUI;
  items: PrintPrescriptionItem[];
}) {
  if (items.length === 0) {
    return (
      <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
        No prescribed items.
      </div>
    );
  }

  if (template.itemsLayout === 'numbered_list') {
    return (
      <ol
        style={{
          margin: 0,
          paddingLeft: 22,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {items.map((item, idx) => (
          <li key={idx} style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 600 }}>
              {item.itemName}
              {settings.showItemDetails && item.itemDetails ? (
                <span style={{ fontWeight: 400, color: '#475569' }}> — {item.itemDetails}</span>
              ) : null}
            </div>
            <div style={{ color: '#475569' }}>
              {[
                item.doseQuantity,
                formatScheduleSummary(item),
                formatTiming(item.intakeTiming),
                formatDuration(item),
              ]
                .filter((v) => v && v !== '—')
                .join(' · ')}
            </div>
            {settings.showInstructions && item.instructions && (
              <div style={{ color: '#0f172a' }}>↳ {item.instructions}</div>
            )}
          </li>
        ))}
      </ol>
    );
  }

  // Table layout
  const cellPad = template.density === 'compact' ? '6px 8px' : '8px 10px';
  const headerCellStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: cellPad,
    fontSize: 11,
    fontWeight: 600,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid #e2e8f0',
  };
  const cellStyle: React.CSSProperties = {
    padding: cellPad,
    fontSize: 12,
    color: '#0f172a',
    borderBottom: '1px solid #f1f5f9',
    verticalAlign: 'top',
  };

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12,
      }}
    >
      <thead>
        <tr>
          <th style={{ ...headerCellStyle, width: 28 }}>#</th>
          <th style={headerCellStyle}>Item</th>
          {settings.showItemDetails && <th style={headerCellStyle}>Details</th>}
          <th style={{ ...headerCellStyle, width: 60 }}>Dose</th>
          <th style={{ ...headerCellStyle, width: 80 }}>Schedule</th>
          <th style={{ ...headerCellStyle, width: 110 }}>When</th>
          <th style={{ ...headerCellStyle, width: 80 }}>Duration</th>
          {settings.showInstructions && <th style={headerCellStyle}>Instructions</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => (
          <tr key={idx}>
            <td style={{ ...cellStyle, color: '#64748b' }}>{idx + 1}</td>
            <td style={{ ...cellStyle, fontWeight: 600 }}>{item.itemName}</td>
            {settings.showItemDetails && (
              <td style={{ ...cellStyle, color: '#475569' }}>{item.itemDetails || '—'}</td>
            )}
            <td style={cellStyle}>{item.doseQuantity || '—'}</td>
            <td style={cellStyle}>{formatScheduleSummary(item)}</td>
            <td style={cellStyle}>{formatTiming(item.intakeTiming)}</td>
            <td style={cellStyle}>{formatDuration(item)}</td>
            {settings.showInstructions && (
              <td style={{ ...cellStyle, color: '#0f172a' }}>{item.instructions || '—'}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Footer ───

function Footer({
  settings,
  template,
  doctorName,
  doctorCredentials,
}: {
  settings: PrescriptionPrintSettingsUI;
  template: SystemTemplate;
  doctorName: string;
  doctorCredentials: string;
}) {
  const showDoctorFooter = settings.showDoctorFooter;
  const showSignature = settings.showSignatureBlock;
  const showFooterDoctor = template.doctorBlock === 'footer' && (doctorName || doctorCredentials);

  if (!showDoctorFooter && !showSignature && !showFooterDoctor && !settings.footerText) {
    return null;
  }

  return (
    <footer
      style={{
        marginTop: 'auto',
        paddingTop: 16,
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        gap: 16,
        justifyContent: 'space-between',
        alignItems: 'flex-end',
      }}
    >
      <div style={{ fontSize: 11, color: '#64748b', maxWidth: '60%' }}>
        {settings.footerText || ''}
      </div>

      <div style={{ textAlign: 'right' }}>
        {showSignature && (
          <div
            style={{
              minWidth: 180,
              borderTop: '1px solid #0f172a',
              paddingTop: 4,
              fontSize: 11,
              color: '#475569',
            }}
          >
            Doctor's Signature
          </div>
        )}
        {(showDoctorFooter || showFooterDoctor) && (
          <div style={{ marginTop: showSignature ? 6 : 0 }}>
            {doctorName && <div style={{ fontWeight: 700, fontSize: 12 }}>{doctorName}</div>}
            {doctorCredentials && (
              <div style={{ fontSize: 11, color: '#475569' }}>{doctorCredentials}</div>
            )}
          </div>
        )}
      </div>
    </footer>
  );
}

// ─── Classic Clinical layout (PRD.184 — partner-style letterhead) ───

interface ClassicClinicalProps {
  settings: PrescriptionPrintSettingsUI;
  doctorName: string;
  doctorCredentials: string;
  patient: PrintPatient;
  date: string;
  items: PrintPrescriptionItem[];
  complaint?: string;
  weightAndBp?: string;
  accent: string;
  previewMode?: boolean;
}

function ClassicClinicalLayout({
  settings,
  doctorName,
  doctorCredentials,
  patient,
  date,
  items,
  complaint,
  weightAndBp,
  accent,
  previewMode,
}: ClassicClinicalProps) {
  const logoUrl = resolveLogoUrl(settings);

  return (
    <div
      data-prescription-print="true"
      style={{
        width: '210mm',
        minHeight: '297mm',
        background: '#ffffff',
        color: '#1f2937',
        fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        fontSize: 12,
        lineHeight: 1.45,
        padding: '14mm 14mm 12mm',
        boxSizing: 'border-box',
        boxShadow: previewMode ? '0 1px 3px rgba(15, 23, 42, 0.12)' : undefined,
        border: previewMode ? '1px solid rgba(15, 23, 42, 0.08)' : undefined,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        position: 'relative',
      }}
    >
      <ClassicHeader
        settings={settings}
        logoUrl={logoUrl}
        doctorName={doctorName}
        doctorCredentials={doctorCredentials}
        accent={accent}
      />

      <ClassicPatientStrip
        patient={patient}
        date={date}
        weightAndBp={weightAndBp}
        showDemographics={settings.showPatientDemographics}
      />

      <div
        style={{
          textAlign: 'center',
          fontSize: 18,
          fontWeight: 700,
          color: accent,
          letterSpacing: '0.18em',
          marginTop: 6,
        }}
      >
        PRESCRIPTION
      </div>

      {settings.showComplaint && complaint && (
        <div style={{ fontSize: 12, lineHeight: 1.55 }}>
          <span style={{ fontWeight: 700 }}>Complaint: </span>
          <span style={{ fontStyle: 'italic', color: '#374151' }}>{complaint}</span>
        </div>
      )}

      <ClassicItemsTable
        items={items}
        accent={accent}
        showItemDetails={settings.showItemDetails}
        showInstructions={settings.showInstructions}
      />

      <ClassicSignature
        showSignature={settings.showSignatureBlock}
        showDoctorFooter={settings.showDoctorFooter}
        doctorName={doctorName}
      />

      <ClassicFooter settings={settings} accent={accent} />
    </div>
  );
}

// ─── Classic header ───

function ClassicHeader({
  settings,
  logoUrl,
  doctorName,
  doctorCredentials,
  accent,
}: {
  settings: PrescriptionPrintSettingsUI;
  logoUrl: string;
  doctorName: string;
  doctorCredentials: string;
  accent: string;
}) {
  const isHorizontal = settings.logoSource === 'horizontal';
  const logoHeight = isHorizontal ? 56 : 60;

  return (
    <header style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Clinic logo"
              style={{
                display: 'block',
                flexShrink: 0,
                height: logoHeight,
                width: 'auto',
                maxWidth: isHorizontal ? 260 : logoHeight,
                objectFit: 'contain',
              }}
            />
          )}
          {settings.clinicName && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  letterSpacing: '0.02em',
                  color: accent,
                  lineHeight: 1.05,
                }}
              >
                {settings.clinicName}
              </div>
              {settings.footerText && (
                <div
                  style={{
                    fontSize: 11,
                    color: '#6b7280',
                    letterSpacing: '0.18em',
                    marginTop: 2,
                  }}
                >
                  {settings.footerText.toUpperCase()}
                </div>
              )}
            </div>
          )}
        </div>

        {(doctorName || doctorCredentials) && (
          <div
            style={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {doctorName && (
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
                Dr. {doctorName.replace(/^Dr\.?\s*/i, '')}
              </div>
            )}
            {doctorCredentials && (
              <div style={{ fontSize: 11, color: '#374151' }}>{doctorCredentials}</div>
            )}
            {settings.clinicPhone && (
              <div
                style={{
                  fontSize: 11,
                  color: '#374151',
                  display: 'inline-flex',
                  gap: 4,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <PhoneIcon color={accent} /> {settings.clinicPhone}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ height: 1, background: '#d1d5db' }} />
    </header>
  );
}

// ─── Classic patient strip ───

function ClassicPatientStrip({
  patient,
  date,
  weightAndBp,
  showDemographics,
}: {
  patient: PrintPatient;
  date: string;
  weightAndBp?: string;
  showDemographics: boolean;
}) {
  const ageGender = [patient.age, patient.sex].filter(Boolean).join(' / ');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          rowGap: 4,
          columnGap: 24,
          fontSize: 12,
        }}
      >
        {showDemographics && <ClassicLabelValue label="Patient Name" value={patient.name || '—'} />}
        <ClassicLabelValue label="Date" value={formatPrintDate(date)} />
        {showDemographics && <ClassicLabelValue label="Age / Gender" value={ageGender || '—'} />}
        {weightAndBp && <ClassicLabelValue label="Weight / BP" value={weightAndBp} />}
      </div>
      <div style={{ height: 1, background: '#d1d5db' }} />
    </div>
  );
}

function ClassicLabelValue({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ minWidth: 110, color: '#374151', fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#6b7280' }}>:</span>
      <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// ─── Classic items table ───

function ClassicItemsTable({
  items,
  accent,
  showItemDetails,
  showInstructions,
}: {
  items: PrintPrescriptionItem[];
  accent: string;
  showItemDetails: boolean;
  showInstructions: boolean;
}) {
  if (items.length === 0) {
    return (
      <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
        No prescribed items.
      </div>
    );
  }

  const headerCell: React.CSSProperties = {
    padding: '10px 8px',
    textAlign: 'center',
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 600,
    background: accent,
    borderRight: '1px solid rgba(255,255,255,0.18)',
  };
  const cell: React.CSSProperties = {
    padding: '10px 8px',
    fontSize: 12,
    color: '#111827',
    borderRight: '1px solid #d1d5db',
    borderBottom: '1px solid #d1d5db',
    verticalAlign: 'top',
  };
  const cellLast: React.CSSProperties = { ...cell, borderRight: 'none' };

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '1px solid #d1d5db',
        borderRadius: 4,
        overflow: 'hidden',
        fontSize: 12,
      }}
    >
      <thead>
        <tr>
          <th style={{ ...headerCell, width: 50 }}>S. No.</th>
          <th style={{ ...headerCell, textAlign: 'left' }}>Medicine Name</th>
          <th style={{ ...headerCell, width: 170 }}>
            Dosage
            <div style={{ fontWeight: 400, fontSize: 11, marginTop: 2 }}>(M - A - N)</div>
          </th>
          <th style={{ ...headerCell, width: 90 }}>Duration</th>
          <th style={{ ...headerCell, borderRight: 'none', textAlign: 'left' }}>Instructions</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => {
          const last = idx === items.length - 1;
          const lastRowExtras: React.CSSProperties = last ? { borderBottom: 'none' } : {};
          return (
            <tr key={idx}>
              <td
                style={{
                  ...cell,
                  ...lastRowExtras,
                  textAlign: 'center',
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                {idx + 1}.
              </td>
              <td style={{ ...cell, ...lastRowExtras }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                  {item.itemName || '—'}
                </div>
                {showItemDetails && item.itemDetails && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    {item.itemDetails}
                  </div>
                )}
              </td>
              <td style={{ ...cell, ...lastRowExtras }}>
                <DosageCells item={item} accent={accent} />
              </td>
              <td style={{ ...cell, ...lastRowExtras, textAlign: 'center' }}>
                {item.durationValue != null
                  ? `${item.durationValue} ${DURATION_UNIT_LABEL[item.durationUnit ?? 'days'] ?? item.durationUnit ?? 'Days'}`
                  : '—'}
              </td>
              <td style={{ ...cellLast, ...lastRowExtras, color: '#374151' }}>
                {showInstructions && item.instructions ? item.instructions : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─── Dosage cells (M-A-N boxes + inline timing text) ───

function DosageCells({ item, accent }: { item: PrintPrescriptionItem; accent: string }) {
  const hasAny = item.scheduleMorning || item.scheduleAfternoon || item.scheduleNight;
  if (!hasAny) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, color: '#9ca3af' }}>
        <span>—</span>
        <span>—</span>
        <span>—</span>
      </div>
    );
  }
  const cell = (active: boolean) => (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 4,
        border: '1px solid #d1d5db',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        color: active ? '#111827' : '#9ca3af',
        background: '#ffffff',
      }}
    >
      {active ? 1 : 0}
    </div>
  );
  // Inline timing text on the same row as the M-A-N boxes keeps each
  // prescription line compact (one row instead of two). Only render
  // when a meaningful timing is set — 'anytime' or missing still
  // collapses to just the boxes.
  const showTiming = item.intakeTiming && item.intakeTiming !== 'anytime';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {cell(item.scheduleMorning)}
        <span style={{ color: '#9ca3af' }}>-</span>
        {cell(item.scheduleAfternoon)}
        <span style={{ color: '#9ca3af' }}>-</span>
        {cell(item.scheduleNight)}
      </div>
      {showTiming && (
        <span style={{ fontSize: 11, color: accent, whiteSpace: 'nowrap' }}>
          {INTAKE_TIMING_LABEL[item.intakeTiming as string] ?? item.intakeTiming}
        </span>
      )}
    </div>
  );
}

// ─── Classic signature block ───

function ClassicSignature({
  showSignature,
  showDoctorFooter,
  doctorName,
}: {
  showSignature: boolean;
  showDoctorFooter: boolean;
  doctorName: string;
}) {
  if (!showSignature && !showDoctorFooter) return null;
  if (!doctorName) return null;
  return (
    <div
      style={{
        marginTop: 24,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div style={{ textAlign: 'right' }}>
        {showDoctorFooter && (
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
            Dr. {doctorName.replace(/^Dr\.?\s*/i, '')}
          </div>
        )}
        {showSignature && (
          <div
            style={{
              marginTop: 4,
              fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive',
              fontStyle: 'italic',
              fontSize: 22,
              color: '#1f2937',
            }}
          >
            {doctorName}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Classic footer (3 columns, iconified) ───

function ClassicFooter({
  settings,
  accent,
}: {
  settings: PrescriptionPrintSettingsUI;
  accent: string;
}) {
  const cellStyle: React.CSSProperties = {
    fontSize: 11,
    color: '#374151',
    lineHeight: 1.45,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
  };
  return (
    <footer
      style={{
        marginTop: 'auto',
        paddingTop: 12,
        borderTop: `1px solid #d1d5db`,
        display: 'grid',
        gridTemplateColumns: '1.2fr 1fr 1fr',
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {settings.clinicName && (
          <div style={{ fontWeight: 700, color: '#111827' }}>{settings.clinicName}</div>
        )}
        {settings.footerText && (
          <div style={{ color: '#6b7280', fontSize: 11 }}>{settings.footerText}</div>
        )}
        {settings.clinicAddress && (
          <div style={{ ...cellStyle, marginTop: 4 }}>
            <span style={{ color: accent, marginTop: 1 }}>
              <PinIcon color={accent} />
            </span>
            <span style={{ whiteSpace: 'pre-line' }}>{settings.clinicAddress}</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {settings.clinicPhone && (
          <div style={cellStyle}>
            <PhoneIcon color={accent} /> {settings.clinicPhone}
          </div>
        )}
        {settings.clinicEmail && (
          <div style={cellStyle}>
            <MailIcon color={accent} /> {settings.clinicEmail}
          </div>
        )}
        {settings.clinicWebsite && (
          <div style={cellStyle}>
            <GlobeIcon color={accent} /> {settings.clinicWebsite}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={cellStyle}>
          <ClockIcon color={accent} /> Mon - Sat: 10:00 AM - 8:00 PM
        </div>
        <div style={{ ...cellStyle, paddingLeft: 18 }}>Sunday: 10:00 AM - 2:00 PM</div>
      </div>
    </footer>
  );
}

// ─── Inline footer / header icons ───

function PinIcon({ color }: { color: string }) {
  return (
    <svg width={12} height={14} viewBox="0 0 12 14" fill="none" aria-hidden>
      <path
        d="M6 13 C6 13 1 8.2 1 5 a5 5 0 0 1 10 0 c0 3.2 -5 8 -5 8 z"
        stroke={color}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="6" cy="5" r="1.6" stroke={color} strokeWidth="1.2" />
    </svg>
  );
}

function PhoneIcon({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M2 2 a1 1 0 0 1 1 -1 h1.6 a1 1 0 0 1 1 0.7 l0.5 1.7 a1 1 0 0 1 -0.3 1 l-0.7 0.6 a7 7 0 0 0 3.2 3.2 l0.6 -0.7 a1 1 0 0 1 1 -0.3 l1.7 0.5 a1 1 0 0 1 0.7 1 V10 a1 1 0 0 1 -1 1 c-4.4 0 -9 -4.6 -9 -9 z"
        stroke={color}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MailIcon({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 14 12" fill="none" aria-hidden>
      <rect x="1" y="2" width="12" height="9" rx="1.4" stroke={color} strokeWidth="1.2" />
      <path d="M1.6 3 L7 7 L12.4 3" stroke={color} strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}

function ClockIcon({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden>
      <circle cx="6" cy="6" r="5" stroke={color} strokeWidth="1.2" />
      <path d="M6 3 L6 6 L8 7" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function GlobeIcon({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden>
      <circle cx="6" cy="6" r="5" stroke={color} strokeWidth="1.2" />
      <ellipse cx="6" cy="6" rx="2.2" ry="5" stroke={color} strokeWidth="1.2" />
      <path d="M1 6 L11 6" stroke={color} strokeWidth="1.2" />
    </svg>
  );
}
